/**
 * THE VITRINE — gallery manifest.
 *
 * The wall at /gallery. One entry per frame, ordered as an editorial sequence
 * rather than by file name: subject alternates, film alternates with still, and
 * every eighth position yields to a full-width feature slab so the grid breathes.
 *
 * WHY THE RATIOS ARE WHAT THEY ARE
 * Seven of the eight films on disk are vertical 9:16 social cuts (1280×2276 and
 * 720×1280). So the wall is a portrait grid at 4:5 — a crop the vertical footage
 * survives without losing its centre — interrupted by landscape feature slabs at
 * 21:9, which is the only ratio the wide frames actually are. The footage chose
 * the layout, not the other way round.
 *
 * WORD BUDGET (enforced by src/test/gallery.test.ts)
 * label ≤ 3 words · meta ≤ 5 words · no prose anywhere · chrome ≤ 20 words.
 * `alt` is deliberately exempt and deliberately full — accessibility and search
 * are never sacrificed to the aesthetic.
 *
 * PROVENANCE — READ THIS BEFORE EDITING A LABEL
 * Every frame here was opened and looked at. That matters more than it sounds:
 * projects.ts pairs the field-*.mp4 clips with a "southern Alberta industrial"
 * narrative, but the footage is land-clearing and forestry mulching, and
 * hero-aerial-poster.jpg is a community park rather than the ridgeline its
 * filename suggests. Descriptions here follow the frame, not the filename and
 * not the surrounding story.
 *
 * Where projects.ts already carries an accurate authored description, that
 * wording is reused verbatim so the two files cannot drift. Where it does not,
 * the description is written from the image itself.
 *
 * `meta` therefore claims only what is verifiable: a place and year when
 * projects.ts attributes the frame to a project, and otherwise the subject and
 * the conditions visible in the frame. No invented locations, no invented dates.
 */

/**
 * A media-typed source. There is deliberately NO `media` key: iOS Safari has a
 * long-standing bug where a non-matching `media` attribute on the first
 * <source> leaves the element with no decoded media, stuck on the poster
 * forever. Viewport selection happens in JS — see VitrineDirector.
 */
export interface VitrineSource {
  src: string;
  type: string;
}

/** 4:5 in the grid, 21:9 when promoted to a feature slab. */
export type VitrineRatio = "portrait" | "scope";

/**
 * Two bodies of work share one wall: the aerial films shot by Fly4MEdia, and
 * the street/landscape stills from the partner studio. They read as one reel at
 * /gallery and split cleanly by this tag when each gets its own route.
 */
export type VitrineCollection = "aerial" | "photography";


export interface VitrineClip {
  /** Stable, slug-cased. React key, deep-link hash, and director registry key. */
  id: string;
  /** ≤ 3 words. */
  label: string;
  /** ≤ 5 words. Place · year where attributable, otherwise subject · condition. */
  meta: string;
  /**
   * The master image, repo-relative. Only two things read it: the variant
   * generator (scripts/generate-gallery-images.mjs) and the test that asserts
   * it still exists. Nothing ships this file — the browser gets the WebP
   * variants derived from it.
   */
  sourceFile: string;
  /** Intrinsic dimensions of the master. Drives aspect-ratio, so CLS stays 0. */
  posterW: number;
  posterH: number;
  /** Absent ⇒ a stills frame. Still a first-class tile. */
  sources?: VitrineSource[];
  /** Lighter cut served to coarse pointers. Selected in JS, never via `media`. */
  mobileSources?: VitrineSource[];
  ratio: VitrineRatio;
  /** Full and descriptive. Exempt from the word budget by design. */
  alt: string;
  /** Art-directed crop for sources whose subject sits off-centre. */
  objectPosition?: string;
  /** Optional cross-link to the case study. Never required. */
  projectSlug?: string;
  /**
   * Which wall a frame belongs to. Absent means "aerial" — the Fly4MEdia drone
   * work that was the whole gallery before the street/landscape stills arrived.
   * One field, so splitting the wall into two routes needs no data migration.
   */
  collection?: VitrineCollection;

}

/**
 * THE AERIAL WALL — /gallery/aerial. The drone films and stills shot by
 * Fly4MEdia. This was the entire gallery before the stills arrived, and its
 * order and ratios are unchanged: nothing about the split re-sequences it.
 */
const AERIAL_CLIPS: VitrineClip[] = [
  {
    id: "above-the-canopy",
    label: "Above the Canopy",
    meta: "Canmore · 2026",
    sourceFile: "public/work/canmore/canmore-1-poster.jpg",
    posterW: 1280,
    posterH: 2276,
    sources: [{ src: "/work/canmore/canmore-1.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "Top-down aerial of Alberta pine and larch canopy meeting an early-spring snowline.",
    projectSlug: "canmore-heights",
  },
  {
    id: "mountain-pass",
    label: "Mountain Pass",
    meta: "Icefields Parkway · 2025",
    sourceFile: "src/assets/work-01-mountain-road.jpg",
    posterW: 1280,
    posterH: 1600,
    ratio: "portrait",
    alt: "Aerial looking down a two-lane highway winding through a snow-dusted mountain valley at dusk, forested slopes falling away to snow-capped peaks.",
    projectSlug: "northern-roads",
  },
  {
    id: "load-out",
    label: "Load Out",
    meta: "Foothills · Summer",
    sourceFile: "public/work/hauling/hauling-1-poster.jpg",
    posterW: 720,
    posterH: 1280,
    sources: [{ src: "/work/hauling/hauling-1.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "Aerial following a flatbed trailer carrying a compact track loader along a paved road through green summer forest.",
    projectSlug: "hauling-the-foothills",
  },
  {
    id: "cedar-planks",
    label: "Cedar Planks",
    meta: "Lake Minnewanka · 2024",
    sourceFile: "src/assets/cs/lake/dock-detail.jpg",
    posterW: 1024,
    posterH: 1024,
    ratio: "portrait",
    alt: "Editorial detail of weathered cedar dock planks meeting still emerald alpine lake water at dawn with light morning mist.",
    projectSlug: "above-the-lake",
  },
  {
    id: "clearing",
    label: "Clearing",
    meta: "Alberta · Winter",
    sourceFile: "public/work/field/field-2-poster.jpg",
    posterW: 720,
    posterH: 1280,
    sources: [{ src: "/work/field/field-2.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "Compact track loader running a mulching head through brush in a snowy stand of pines, wood chips throwing in low winter sun.",
    projectSlug: "field-and-frequency",
  },
  {
    id: "bark",
    label: "Bark",
    meta: "Canmore · 2026",
    sourceFile: "src/assets/cs/canmore/bark-macro.jpg",
    posterW: 1024,
    posterH: 1024,
    ratio: "portrait",
    alt: "Editorial macro of weathered lodgepole pine bark with a single resin bead in soft natural light.",
    projectSlug: "canmore-heights",
  },
  {
    id: "first-light",
    label: "First Light",
    meta: "Alberta · Sunrise",
    sourceFile: "public/hero/hero-drone-poster.jpg",
    posterW: 1920,
    posterH: 1080,
    sources: [
      { src: "/hero/hero-drone.webm", type: "video/webm" },
      { src: "/hero/hero-drone.mp4", type: "video/mp4" },
    ],
    mobileSources: [{ src: "/hero/hero-drone-mobile.mp4", type: "video/mp4" }],
    ratio: "scope",
    alt: "Aerial above a low cloud layer at sunrise, forested ridges breaking through the mist with snow-capped Rockies along the horizon.",
  },
  {
    id: "open-water",
    label: "Open Water",
    meta: "Lake Minnewanka · 2024",
    sourceFile: "src/assets/work-03-lake-boat.jpg",
    posterW: 1280,
    posterH: 1600,
    ratio: "portrait",
    alt: "Single white boat cutting a wake straight across deep teal water, photographed from directly overhead.",
    projectSlug: "above-the-lake",
  },
  {
    id: "mulcher",
    label: "Mulcher",
    meta: "Alberta · Winter",
    sourceFile: "public/work/field/field-3-poster.jpg",
    posterW: 404,
    posterH: 720,
    sources: [{ src: "/work/field/field-3.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "Tracked loader with a mulching head working a cleared line through snowy pine forest, cut timber lying on the ground.",
    projectSlug: "field-and-frequency",
  },
  {
    id: "mile-marker",
    label: "Mile Marker",
    meta: "Icefields Parkway · 2025",
    sourceFile: "src/assets/cs/northern/mile-marker.jpg",
    posterW: 1024,
    posterH: 1024,
    ratio: "portrait",
    alt: "Frosted unbranded green metal road sign post on the Icefields Parkway at first light, distant pine and snowline.",
    projectSlug: "northern-roads",
  },
  {
    id: "through-the-trunks",
    label: "Through the Trunks",
    meta: "Canmore · 2026",
    sourceFile: "public/work/canmore/canmore-2-poster.jpg",
    posterW: 1280,
    posterH: 2276,
    sources: [{ src: "/work/canmore/canmore-2.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "Low pass at head height between spruce trunks over a thickly mulched forest floor on a Canmore acreage.",
    projectSlug: "canmore-heights",
  },
  {
    id: "weathered-steel",
    label: "Weathered Steel",
    meta: "Southern Alberta · 2026",
    sourceFile: "src/assets/cs/field/weathered-steel.jpg",
    posterW: 1024,
    posterH: 1024,
    ratio: "portrait",
    alt: "Editorial detail of weathered galvanised steel cladding on a southern Alberta industrial outbuilding under winter overcast light.",
    projectSlug: "field-and-frequency",
  },
  {
    id: "two-lane",
    label: "Two Lane",
    meta: "Foothills · Summer",
    sourceFile: "public/work/hauling/hauling-2-poster.jpg",
    posterW: 720,
    posterH: 1280,
    sources: [{ src: "/work/hauling/hauling-2.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "Second aerial pass following the load along the foothills two-lane.",
    projectSlug: "hauling-the-foothills",
  },
  {
    id: "on-the-dock",
    label: "On the Dock",
    meta: "Lake Minnewanka · 2024",
    sourceFile: "src/assets/cs-lake-2.jpg",
    posterW: 1280,
    posterH: 1600,
    ratio: "portrait",
    alt: "Person standing on a wooden dock at a still alpine lake.",
    projectSlug: "above-the-lake",
  },
  {
    id: "glacial",
    label: "Glacial",
    meta: "Icefields Parkway · 2025",
    sourceFile: "src/assets/cs/northern/glacial-reflection.jpg",
    posterW: 1920,
    posterH: 1080,
    ratio: "scope",
    alt: "Still turquoise glacial lake at dawn in the Canadian Rockies with thin ice floes and mountain reflection.",
    projectSlug: "northern-roads",
  },
  {
    id: "the-rig",
    label: "The Rig",
    meta: "Alberta · Summer",
    sourceFile: "public/work/field/field-1-poster.jpg",
    posterW: 720,
    posterH: 1280,
    sources: [{ src: "/work/field/field-1.mp4", type: "video/mp4" }],
    ratio: "portrait",
    alt: "White dually pickup parked on a gravel drive in front of a dark equipment shop, summer trees and a trailer behind.",
    projectSlug: "field-and-frequency",
  },
  {
    id: "access-track",
    label: "Access Track",
    meta: "Canmore · 2026",
    sourceFile: "src/assets/cs/canmore/dusk-access-track.jpg",
    posterW: 1920,
    posterH: 1080,
    ratio: "portrait",
    alt: "Narrow gravel access track curving into pine and larch, blue-hour light, distant Bow Valley ridge.",
    projectSlug: "canmore-heights",
  },
  {
    id: "ice-floes",
    label: "Ice Floes",
    meta: "Icefields Parkway · 2025",
    sourceFile: "src/assets/cs-northern-2.jpg",
    posterW: 1280,
    posterH: 1600,
    ratio: "portrait",
    alt: "Aerial of a turquoise glacial lake carrying thin ice floes.",
    projectSlug: "northern-roads",
  },
  {
    id: "structure",
    label: "Structure",
    meta: "Aerial · Architecture",
    sourceFile: "src/assets/work-02-architecture.jpg",
    posterW: 1280,
    posterH: 1600,
    ratio: "portrait",
    alt: "Modern multi-storey house on a forested slope in heavy fog, dark conifers crowding the structure.",
  },
  {
    id: "seam-in-snow",
    label: "Seam in Snow",
    meta: "Southern Alberta · 2026",
    sourceFile: "src/assets/cs/field/seam-in-snow.jpg",
    posterW: 1920,
    posterH: 1080,
    ratio: "portrait",
    alt: "Top-down photograph of a single tire-track seam pressed into fresh snow over hard prairie ground at first light.",
    projectSlug: "field-and-frequency",
  },
  {
    id: "empty-shoulder",
    label: "Empty Shoulder",
    meta: "Icefields Parkway · 2025",
    sourceFile: "src/assets/cs/northern/empty-shoulder.jpg",
    posterW: 1920,
    posterH: 1080,
    ratio: "portrait",
    alt: "Empty Icefields Parkway shoulder before sunrise, fresh tire tracks in light snow curving toward the Canadian Rockies.",
    projectSlug: "northern-roads",
  },
  {
    id: "turbine",
    label: "Turbine",
    meta: "Aerial · Energy",
    sourceFile: "src/assets/work-04-industrial.jpg",
    posterW: 1280,
    posterH: 1600,
    ratio: "portrait",
    alt: "Aerial view of wind turbines standing over farmland with long rows of solar arrays catching low sunset light.",
  },
  {
    id: "still-water",
    label: "Still Water",
    meta: "Lake Minnewanka · 2024",
    sourceFile: "src/assets/cs/lake/water-surface.jpg",
    posterW: 1920,
    posterH: 1080,
    ratio: "scope",
    alt: "Glass-still alpine lake at first light with concentric ripples and pastel mountain reflection.",
    projectSlug: "above-the-lake",
  },
];

/**
 * THE PHOTOGRAPHY WALL — /gallery/photography.
 *
 * Street and landscape stills from the partner studio, plus the Fly4MEdia field
 * frames — mountain, ski-touring and camp photography shot on the ground. Every
 * frame here was opened and looked at, and
 * the copy follows the frame: no place is named that the picture does not
 * prove, and no date is claimed at all, because none exists in the repo. So
 * `meta` is subject · condition throughout — the one honest register available.
 *
 * Sequenced, not sorted: portrait and landscape alternate, city alternates with
 * land, and the genuinely wide frames are promoted to 21:9 feature slabs
 * so a 37-frame wall breathes instead of drumming.
 *
 * Masters are 1284–2000px on the long edge; the WebP ladder is generated by
 * `npm run images` and committed to public/gallery.
 */
const PHOTOGRAPHY_CLIPS: VitrineClip[] = [
  {
    id: "arc-traffic",
    label: "Arc",
    meta: "Monument · traffic blur",
    sourceFile: "src/assets/gallery/semper/arc-traffic.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Triumphal stone arch under bright cloud with cars and a bus streaking past in motion blur across the foreground.",
  },
  {
    id: "crossing-shadow",
    label: "Crossing",
    meta: "Overhead · hard shadow",
    sourceFile: "src/assets/gallery/semper/crossing-shadow.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "High-contrast black and white view looking straight down on a single car crossing striped pedestrian markings in raking light.",
  },
  {
    id: "alpine-narrows",
    label: "Narrows",
    meta: "Valley · summer haze",
    sourceFile: "src/assets/gallery/semper/alpine-narrows.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "scope",
    alt: "Steep green mountain walls closing in on a narrow blue lake far below, softened by summer haze.",
  },
  {
    id: "descent-tarn",
    label: "Descent",
    meta: "Alpine tarn · storm light",
    sourceFile: "src/assets/gallery/field/descent-tarn.jpg",
    posterW: 1440,
    posterH: 617,
    ratio: "scope",
    alt: "A lone hiker crossing a wide ochre scree slope high above a turquoise tarn, with grey limestone benches and a glacier lost in storm cloud beyond.",
  },
  {
    id: "red-hour",
    label: "Red Hour",
    meta: "Camp · headlamp red",
    sourceFile: "src/assets/gallery/field/red-hour.jpg",
    posterW: 1284,
    posterH: 1453,
    ratio: "portrait",
    alt: "Four people eating around a wooden picnic table in the dark, lit only by red headlamps and the blue flame of a camp stove.",
  },
  {
    id: "skin-track",
    label: "Skin Track",
    meta: "Bowl · bluebird",
    sourceFile: "src/assets/gallery/field/skin-track.jpg",
    posterW: 1440,
    posterH: 1920,
    ratio: "portrait",
    alt: "A single skin track curving up an untouched snow bowl toward a corniced col, two small ski tourers ahead under a deep bluebird sky.",
  },
  {
    id: "last-light",
    label: "Last Light",
    meta: "Ridge · alpenglow",
    sourceFile: "src/assets/gallery/field/last-light.jpg",
    posterW: 1284,
    posterH: 550,
    ratio: "scope",
    alt: "Alpenglow catching a distant limestone summit above a pale rock ridge, seen across an alpine meadow of grass and pink wildflowers at sunset.",
  },
  {
    id: "castle-keep",
    label: "Keep",
    meta: "Tiered roofs · storm light",
    sourceFile: "src/assets/gallery/semper/castle-keep.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "Tiered castle keep with gold-trimmed eaves rising above a stone wall and dark trees under a heavy grey sky.",
  },
  {
    id: "crosswalk-child",
    label: "Small Steps",
    meta: "Crosswalk · asphalt",
    sourceFile: "src/assets/gallery/semper/crosswalk-child.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A child in a white shirt crouching on a painted crosswalk stripe beside a manhole cover, seen from behind.",
  },
  {
    id: "elevated-line",
    label: "Elevated Line",
    meta: "Rail over street",
    sourceFile: "src/assets/gallery/semper/elevated-line.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "A commuter train on an elevated line curving between signboard-covered buildings above a busy city intersection.",
  },
  {
    id: "cliff-village",
    label: "Cliff Village",
    meta: "Hill town · clear day",
    sourceFile: "src/assets/gallery/semper/cliff-village.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "scope",
    alt: "Pastel houses stacked on a ridge beneath bare rock spires, with layered blue mountains receding behind.",
  },
  {
    id: "expressway-dusk",
    label: "Expressway",
    meta: "City grid · dusk",
    sourceFile: "src/assets/gallery/semper/expressway-dusk.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "Aerial view over a dense city at dusk with an expressway cutting a lit orange line between towers to the horizon.",
  },
  {
    id: "grassland-horse",
    label: "Grassland",
    meta: "Horse · open pasture",
    sourceFile: "src/assets/gallery/semper/grassland-horse.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A lone brown horse walking through deep green grassland with soft rolling hills behind under overcast light.",
  },
  {
    id: "harbour-ferry",
    label: "Ferry",
    meta: "Harbour · flat water",
    sourceFile: "src/assets/gallery/semper/harbour-ferry.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "A blue and white ferry moored on calm water framed by a concrete overhang, with a hazy city skyline beyond.",
  },
  {
    id: "hay-bales",
    label: "Hay Bales",
    meta: "Field · late summer",
    sourceFile: "src/assets/gallery/semper/hay-bales.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Round hay bales scattered across a mown golden field behind a weathered fence post under a blue sky.",
  },
  {
    id: "hilltop-castle",
    label: "Hilltop",
    meta: "Castle · forest below",
    sourceFile: "src/assets/gallery/semper/hilltop-castle.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A white turreted castle standing on a wooded hill above a wide green valley with pale sky behind.",
  },
  {
    id: "fog-over-ridge",
    label: "Fog Line",
    meta: "Ridge · dissolving cloud",
    sourceFile: "src/assets/gallery/semper/fog-over-ridge.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "scope",
    alt: "A dark forested ridge line dissolving into white fog, with the slope reading as a single silhouette.",
  },
  {
    id: "island-dusk",
    label: "Island Dusk",
    meta: "Bay · sunset",
    sourceFile: "src/assets/gallery/semper/island-dusk.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Silhouetted branches framing a still bay at sunset with distant island headlands under an amber and blue sky.",
  },
  {
    id: "leaf-mass",
    label: "Leaf Mass",
    meta: "Foliage · close study",
    sourceFile: "src/assets/gallery/semper/leaf-mass.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Tight study of broad glossy green leaves overlapping into deep shadow, filling the frame edge to edge.",
  },
  {
    id: "leaning-trees",
    label: "Leaning Trees",
    meta: "Roadside · first light",
    sourceFile: "src/assets/gallery/semper/leaning-trees.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Bare trees leaning over a quiet road with low golden sun burning through the trunks and fallen leaves underfoot.",
  },
  {
    id: "level-crossing",
    label: "Level Crossing",
    meta: "Signals · commuter train",
    sourceFile: "src/assets/gallery/semper/level-crossing.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "A commuter train passing a level crossing behind striped barriers, signal heads and overhead wires against pale sky.",
  },
  {
    id: "light-shaft",
    label: "Light Shaft",
    meta: "Rooftops · raking sun",
    sourceFile: "src/assets/gallery/semper/light-shaft.jpg",
    posterW: 2500,
    posterH: 3750,
    ratio: "portrait",
    alt: "A shaft of low sun raking across a dense field of city rooftops, splitting the frame into lit and shadowed blocks.",
  },
  {
    id: "moored-rowboat",
    label: "Rowboat",
    meta: "Still water · green shade",
    sourceFile: "src/assets/gallery/semper/moored-rowboat.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A wooden rowboat moored on glassy water in deep green shade, ferns and overhanging leaves framing the hull.",
  },
  {
    id: "night-stall",
    label: "Night Stall",
    meta: "Street food · neon",
    sourceFile: "src/assets/gallery/semper/night-stall.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Passers-by crossing in front of a brightly lit street food stall at night, signage and awnings glowing above them.",
  },
  {
    id: "orange-classic",
    label: "Orange Classic",
    meta: "Vintage car · plaza",
    sourceFile: "src/assets/gallery/semper/orange-classic.jpg",
    posterW: 2000,
    posterH: 3000,
    ratio: "portrait",
    alt: "A bright orange vintage car parked on a plaza in front of an ornate stone façade with gilded sculpture above.",
  },
  {
    id: "pan-crossing",
    label: "Pan",
    meta: "Intersection · long pan",
    sourceFile: "src/assets/gallery/semper/pan-crossing.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "scope",
    alt: "Panned exposure of vehicles crossing an intersection, the stone building behind smeared into horizontal streaks.",
  },
  {
    id: "peaks-in-cloud",
    label: "Peaks",
    meta: "Rock · low cloud",
    sourceFile: "src/assets/gallery/semper/peaks-in-cloud.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Serrated grey rock peaks and green gullies with a bank of low white cloud sitting flat across the summits.",
  },
  {
    id: "quiet-path",
    label: "Quiet Path",
    meta: "Brick wall · deep shade",
    sourceFile: "src/assets/gallery/semper/quiet-path.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A single figure walking a shaded path beside an old brick building, glass towers catching sun in the gap beyond.",
  },
  {
    id: "red-taxi",
    label: "Red Taxi",
    meta: "Boulevard · midday",
    sourceFile: "src/assets/gallery/semper/red-taxi.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A red taxi pulling along a tree-lined boulevard past shopfronts and a tiled office block in flat midday light.",
  },
  {
    id: "shaded-wall",
    label: "Shaded Wall",
    meta: "Glass façade · foliage",
    sourceFile: "src/assets/gallery/semper/shaded-wall.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A pale green glass façade half-swallowed by dark foliage, with traffic cones and one figure at the doorway.",
  },
  {
    id: "summit-path",
    label: "Summit Path",
    meta: "Headland · white sky",
    sourceFile: "src/assets/gallery/semper/summit-path.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "A dirt path climbing a grassy headland to a small building and a cross on the summit against a blown-out white sky.",
  },
  {
    id: "town-rooftops",
    label: "Town Rooftops",
    meta: "Valley town · rain coming",
    sourceFile: "src/assets/gallery/semper/town-rooftops.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Metal and tile rooftops of a valley town stepping downhill, with rain cloud pressing on the mountains behind.",
  },
  {
    id: "umbrellas",
    label: "Umbrellas",
    meta: "Rain · crowded crossing",
    sourceFile: "src/assets/gallery/semper/umbrellas.jpg",
    posterW: 2500,
    posterH: 1667,
    ratio: "portrait",
    alt: "Clear and black umbrellas overlapping in the rain as people wait at a crowded city crossing, signage blurred behind.",
  },
  {
    id: "berg-night",
    label: "Berg Night",
    meta: "Glacier lake · red light",
    sourceFile: "src/assets/gallery/field/berg-night.jpg",
    posterW: 1439,
    posterH: 1920,
    ratio: "portrait",
    alt: "Two figures in headlamp red light standing on a stony lakeshore at night, a glaciated peak and its icefall rising grey behind the water.",
  },
  {
    id: "cornice",
    label: "Cornice",
    meta: "Ridgeline · wind crust",
    sourceFile: "src/assets/gallery/field/cornice.jpg",
    posterW: 1440,
    posterH: 1920,
    ratio: "portrait",
    alt: "A single skier in a red jacket climbing a wind-scoured snow ridge, with corniced ridgelines and distant peaks under deep blue sky.",
  },
  {
    id: "basecamp",
    label: "Basecamp",
    meta: "Desert coast · blue hour",
    sourceFile: "src/assets/gallery/field/basecamp.jpg",
    posterW: 1440,
    posterH: 1920,
    ratio: "portrait",
    alt: "A camper van and a converted school bus parked on a gravel beach at blue hour, cactus-covered ridges and hazy spires rising behind them.",
  },
  {
    id: "cirque-still",
    label: "Still Water",
    meta: "Cirque lake · rain",
    sourceFile: "src/assets/gallery/field/still-water.jpg",
    posterW: 1280,
    posterH: 1920,
    ratio: "portrait",
    alt: "A person in a rain jacket standing on a mossy ledge above an emerald cirque lake, spruce forest and a cloud-covered rock wall beyond.",
  },
];

/**
 * Both walls, in one list, each frame tagged.
 *
 * The tag is applied here rather than repeated on 52 objects: it is derived
 * from which array a frame lives in, so the two can never disagree. Everything
 * that wants a single wall goes through `clipsFor`.
 */
export const galleryClips: VitrineClip[] = [
  ...AERIAL_CLIPS.map((c) => ({ ...c, collection: "aerial" as const })),
  ...PHOTOGRAPHY_CLIPS.map((c) => ({ ...c, collection: "photography" as const })),
];

/**
 * WALL RHYTHM
 *
 * A `scope` frame is a full-width feature slab. Two of them back to back stop
 * being features and start being a filmstrip — measured on a 2560px display,
 * three consecutive slabs filled the whole wall with three pictures. So the
 * order the wall receives spaces the slabs out: portrait tiles fill the grid,
 * and a slab is dropped in only once at least six tiles have passed since the
 * last one. Nothing is hidden or duplicated, only sequenced; the lightbox and
 * keyboard traversal read this same array, so visual and logical order agree.
 */
function withWallRhythm(clips: VitrineClip[]): VitrineClip[] {
  const slabs = clips.filter((c) => c.ratio === "scope");
  const tiles = clips.filter((c) => c.ratio !== "scope");
  const out: VitrineClip[] = [];
  let sinceSlab = 0;
  let s = 0;
  for (const tile of tiles) {
    out.push(tile);
    sinceSlab += 1;
    if (s < slabs.length && sinceSlab >= 6) {
      out.push(slabs[s++]);
      sinceSlab = 0;
    }
  }
  // Any slabs left over (more slabs than the grid could space out) close the wall.
  while (s < slabs.length) out.push(slabs[s++]);
  return out;
}

export function clipsFor(collection: VitrineCollection): VitrineClip[] {
  return withWallRhythm(
    galleryClips.filter((c) => (c.collection ?? "aerial") === collection),
  );
}


/**
 * THE THRESHOLD — the copy on /gallery itself.
 *
 * Two words, and the counts. The hub does not describe the work; it asks one
 * question and gets out of the way. Anything longer than this belongs on a wall.
 */
export const GALLERY_HUB_COPY = {
  eyebrow: "Gallery",
  headline: ["Two ways", "of seeing."],
  doors: [
    {
      collection: "aerial" as const,
      label: "Aerial Videography",
      path: "/gallery/aerial",
      note: "From the air",
    },
    {
      collection: "photography" as const,
      label: "Photography",
      path: "/gallery/photography",
      note: "Street and landscape",
    },
  ],
} as const;

/** Per-wall chrome. Same cadence as /work and the homepage: two short lines. */
export const GALLERY_WALL_COPY = {
  aerial: {
    eyebrow: "Aerial Videography",
    headline: ["The reel,", "uncut."],
    path: "/gallery/aerial",
    title: "Aerial Video Gallery — The Reel | Fly4MEdia",
    description:
      "The full aerial reel — drone video and stills from Alberta shoots: Canmore, the Icefields Parkway, Lake Minnewanka, the foothills and southern Alberta.",
  },
  photography: {
    eyebrow: "Photography",
    headline: ["Stills,", "in sequence."],
    path: "/gallery/photography",
    title: "Photography Gallery — Street & Landscape | Fly4MEdia",
    description:
      "A wall of street and landscape photography — city crossings, coastlines, ridgelines and quiet roads, shot on the ground and sequenced as one body of work.",
  },
} as const satisfies Record<
  VitrineCollection,
  {
    eyebrow: string;
    headline: readonly [string, string];
    path: string;
    title: string;
    description: string;
  }
>;

/**
 * RESPONSIVE POSTERS
 *
 * Measured before this existed, phone 390 / Slow 4G / 4x CPU: LCP 6016ms and
 * 4843KB of images, with masters over-fetched up to 6.19x. The masters are
 * 1024–1920px JPEGs; a tile draws at ~310px CSS on a 4-column desktop.
 *
 * Two widths per frame, generated by `npm run images` and committed. Widths are
 * derived from the wall's real layout, not guessed: the largest genuine need is
 * a 1-column phone tile at 2x DPR (~716px), which 840 covers, and 420 serves 1x
 * desktop and small phones. Feature slabs go full-container, so they get 900
 * and 1800.
 */
export const POSTER_WIDTHS: Record<VitrineRatio, readonly number[]> = {
  /*
   * Four rungs. Measured: Lighthouse's mobile viewport is 412px at DPR 1.75, so
   * a one-column tile needs ~637px — and a 420/840 ladder has to round up to
   * 840, shipping a third more pixels than it draws. 675KB of waste across the
   * wall. 640 lands on the real number.
   *
   * 1100 is the top of the room: on a 2560px display the wall runs to a 2200px
   * measure at five columns, so a portrait tile draws ~410px CSS — ~820px at
   * DPR 2, and past that on a 5K panel. Each rung still clamps to the master
   * (posterWidths), so a 1280px master gains it and a smaller one does not.
   */
  portrait: [420, 640, 840, 1100],
  scope: [900, 1400, 1800, 2400],
};


/**
 * The widths that actually exist for a frame.
 *
 * A master is never upscaled, so a target wider than the master clamps to it —
 * `mulcher` is a 404px-wide still and has exactly one variant. The srcset width
 * descriptor has to be the file's real pixel width or the browser picks a
 * candidate believing it is larger than it is and renders it soft. Generator
 * and runtime both derive this from `posterW`, so they cannot disagree.
 */
export function posterWidths(clip: VitrineClip): number[] {
  const capped = POSTER_WIDTHS[clip.ratio].map((w) => Math.min(w, clip.posterW));
  return [...new Set(capped)].sort((a, b) => a - b);
}

/** Largest variant — the `src` fallback for anything that ignores srcset. */
export function posterSrc(clip: VitrineClip): string {
  const widths = posterWidths(clip);
  return `/gallery/${clip.id}-${widths[widths.length - 1]}.webp`;
}

export function posterSrcSet(clip: VitrineClip): string {
  return posterWidths(clip)
    .map((w) => `/gallery/${clip.id}-${w}.webp ${w}w`)
    .join(", ");
}

/**
 * `sizes` describes the tile in CSS pixels EXACTLY, not approximately.
 *
 * It used to say 92vw for the one-column case. The real tile is the viewport
 * minus the container's 24px gutters — 88.3vw at 412px. A 4% overstatement
 * sounds harmless, but srcset selection rounds UP to the next candidate: 92vw
 * at DPR 1.75 asks for 663px, a whisker past the 640 rung, so every tile
 * fetched the 840 anyway. Lighthouse costed that at 675KB.
 *
 * Every breakpoint below is therefore derived from the actual layout — the
 * .vit-measure padding (24 / 40 / 64 / 80px), its 2200px cap, and the grid gap
 * (16 / 20 / 24 / 28px) — rather than eyeballed. The `min(…, 2200px)` is what
 * keeps a 5K display from asking for a tile far wider than the wall can be.
 */
const MEASURE = {
  // (100vw - padding*2) clamped to the room's own maximum width.
  sm: "min(100vw - 48px, 2200px)",
  md: "min(100vw - 80px, 2200px)",
  lg: "min(100vw - 128px, 2200px)",
  xl: "min(100vw - 160px, 2200px)",
} as const;

/**
 * DISPLAY CEILING — a frame is never drawn wider than its pixels allow.
 *
 * Every master here is small by desktop standards: the drone posters come off
 * 720p footage, the field stills top out at 1440px, the partner stills at
 * 2000px. A full-width slab on a 2200px wall draws ~2040 CSS px, which a 2x
 * display resolves at ~4080 device px — twice what the largest file holds. The
 * browser magnifies, and magnification reads as blur.
 *
 * So each frame carries a maximum CSS width of `posterW / 1.5`: enough headroom
 * to stay crisp on a 2x panel (1.5 is the honest floor — 2.0 would shrink the
 * wall for no visible gain, 1.0 goes soft). Tiles centre inside their cell
 * rather than stretching past it.
 */
const QUALITY_FLOOR = 1.5;

/**
 * The floor exists so the ceiling can never sabotage a phone. A one-column tile
 * is ~360px wide; capping a small master below that would centre a shrunken tile
 * in an empty column, which looks like a bug rather than a quality decision.
 */
const MIN_CEILING = 480;

export function maxCssWidth(clip: VitrineClip): number {
  return Math.max(MIN_CEILING, Math.round(clip.posterW / QUALITY_FLOOR));
}

/**
 * A feature slab is the full width of the wall. Only a master with the pixels
 * to survive that treatment keeps it — below 1800px a slab is a magnified
 * 720p frame stretched across a desk monitor, which is the single worst-looking
 * thing on the page. Everything else drops back to a grid tile on desktop.
 */
export function isFeatureEligible(clip: VitrineClip): boolean {
  return clip.ratio === "scope" && clip.posterW >= 1800;
}

/** Wrap a layout width in the frame's own ceiling, so `sizes` never over-asks. */
function capped(expr: string, clip: VitrineClip): string {
  return `min(${expr}, ${maxCssWidth(clip)}px)`;
}

export function posterSizes(clip: VitrineClip): string {
  if (isFeatureEligible(clip)) {
    // A feature slab is the full container width at every breakpoint.
    return [
      `(min-width: 1920px) ${capped(MEASURE.xl, clip)}`,
      `(min-width: 1024px) ${capped(MEASURE.lg, clip)}`,
      `(min-width: 768px) ${capped(MEASURE.md, clip)}`,
      capped(MEASURE.sm, clip),
    ].join(", ");
  }
  return [
    // 6 columns, 28px gaps
    `(min-width: 2400px) ${capped(`calc((${MEASURE.xl} - 140px) / 6)`, clip)}`,
    // 5 columns, 28px gaps
    `(min-width: 1920px) ${capped(`calc((${MEASURE.xl} - 112px) / 5)`, clip)}`,
    // 4 columns, 24px gaps
    `(min-width: 1440px) ${capped(`calc((${MEASURE.lg} - 72px) / 4)`, clip)}`,
    // 3 columns, 24px gaps
    `(min-width: 1024px) ${capped(`calc((${MEASURE.lg} - 48px) / 3)`, clip)}`,
    // 2 columns, one 20px gap
    `(min-width: 768px) ${capped(`calc((${MEASURE.md} - 20px) / 2)`, clip)}`,
    // 2 columns, one 16px gap
    `(min-width: 480px) ${capped(`calc((${MEASURE.sm} - 16px) / 2)`, clip)}`,
    // 1 column: 24px of padding either side
    capped(MEASURE.sm, clip),
  ].join(", ");
}



function statsFor(clips: VitrineClip[]) {
  return {
    frames: clips.length,
    films: clips.filter((c) => c.sources && c.sources.length > 0).length,
  };
}

/** Aggregate — both walls. Read by the sitemap, the SEO graph and the hub. */
export const galleryStats = statsFor(galleryClips);

export function galleryStatsFor(collection: VitrineCollection) {
  return statsFor(clipsFor(collection));
}

/**
 * Index of a frame WITHIN the wall it is being shown on.
 *
 * It takes the list rather than reaching for `galleryClips`, because a deep
 * link, the lightbox counter and arrow traversal all have to mean "seventh of
 * twenty-nine on this wall", never "thirtieth of fifty-two overall".
 */
export function getClipIndex(clips: VitrineClip[], id: string | null): number {
  if (!id) return -1;
  return clips.findIndex((c) => c.id === id);
}
